import React, { useState, useEffect } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Input } from '@progress/kendo-react-inputs';
import { genderOptions } from '../../models/genderOptions';
import './Dialogs.scss';

interface OffboardFormDialogProps {
  isOpen: boolean;
  participantId: string;
  participantGender: string;
  expectedParticipantId: string;
  expectedParticipantGender: string;
  isRejoining?: boolean;
  onParticipantIdChange: (value: string) => void;
  onParticipantGenderChange: (value: string) => void;
  onClose: () => void;
  onContinue: () => void;
}

const OffboardFormDialog: React.FC<OffboardFormDialogProps> = ({
  isOpen,
  participantId,
  participantGender,
  expectedParticipantId,
  expectedParticipantGender,
  isRejoining = false,
  onParticipantIdChange,
  onParticipantGenderChange,
  onClose,
  onContinue,
}) => {
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowErrors(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isParticipantIdValid = participantId.trim() !== '' && participantId === expectedParticipantId;
  const isGenderValid = participantGender.trim() !== '' && participantGender === expectedParticipantGender;
  const isFormValid = isParticipantIdValid && isGenderValid;

  const handleSubmit = () => {
    setShowErrors(true);
    if (isFormValid) {
      onContinue();
    }
  };

  const handleClose = () => {
    setShowErrors(false);
    onClose();
  };

  return (
    <Dialog title={isRejoining ? 'Rejoin Participant' : 'Offboard Participant'} onClose={handleClose} width={700}>
      <div className="offboard-dialog-content">
        <p className="offboard-dialog-text">
          {isRejoining
            ? 'This will allow the participant to continue with the study. To make sure you rejoin the right participant, please provide the data below.'
            : "This means that the participant won't be able to continue the study. However, the data generated will be used for study purposes. You have 7 days to rejoin the participant if needed. To make sure you offboard the right participant, please provide the data below."}
        </p>

        <div className="offboard-field-group">
          <label htmlFor="offboard-participant-id" className="offboard-field-label">
            Participant ID*
          </label>
          <Input
            id="offboard-participant-id"
            value={participantId}
            onChange={(e) => onParticipantIdChange(e.value || '')}
            onPaste={(e) => {
              e.preventDefault();
              return false;
            }}
            autoComplete="off"
            className={`offboard-field-input ${showErrors && !isParticipantIdValid ? 'field-error' : ''}`}
            required
          />
          {showErrors && !isParticipantIdValid && (
            <span className="field-error-message">
              {participantId.trim() === ''
                ? 'Participant ID is required'
                : 'Participant ID does not match the expected participant'}
            </span>
          )}
        </div>

        <div className="offboard-field-group">
          <label htmlFor="offboard-gender" className="offboard-field-label">
            Gender*
          </label>
          <select
            id="offboard-gender"
            value={participantGender}
            onChange={(e) => onParticipantGenderChange(e.target.value)}
            className={`offboard-field-select ${showErrors && !isGenderValid ? 'field-error' : ''}`}
            required
          >
            {genderOptions.map((option, index) => (
              <option key={option} value={index === 0 ? '' : option}>
                {option}
              </option>
            ))}
          </select>
          {showErrors && !isGenderValid && (
            <span className="field-error-message">
              {participantGender.trim() === ''
                ? 'Gender is required'
                : 'Gender does not match the expected participant'}
            </span>
          )}
        </div>

        <div className="offboard-button-container">
          <button className="dashboard-button offboard-dialog-button" onClick={handleSubmit}>
            Continue
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default OffboardFormDialog;
